import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Botao, Cartao, CartaoTopo, Selo, Modal, Campo, Entrada, Medidor, CAMPO_SEGREDO } from "@notes/ui";
import {
  ShieldCheck, Smartphone, KeyRound, Laptop, Trash2, Copy, Printer, Download,
  ChevronRight, CircleCheck, TriangleAlert, LogOut,
} from "lucide-react";
import { Pagina, Secao, Carregando, Erro } from "../shell/Shell.js";
import { useEu } from "../shell/Sessao.js";
import { dataHora } from "../lib/formato.js";

/**
 * SEGURANÇA DA PESSOA.
 *
 * Mora no menu do perfil, e não em Configurações: Configurações é onde a
 * AGÊNCIA se ajusta, e isto aqui é da pessoa. É o mesmo lugar do EnturOS CRM.
 *
 * O assistente de ativação tem cinco passos, um por tela, porque a maior parte
 * de quem ativa nunca ativou antes. A tela conta o porquê antes do como, mostra
 * onde baixar o aplicativo, desenha o QR, pede o código para PROVAR que o
 * aplicativo funciona (sem essa prova a pessoa se tranca fora), e só então
 * entrega os códigos de emergência.
 */

type Acesso = { id: string; ip: string | null; userAgent: string | null; criadoEm: string; expiraEm: string };

async function pedir<T>(caminho: string, corpo?: unknown): Promise<T> {
  const r = await fetch(caminho, {
    method: corpo === undefined ? "GET" : "POST",
    headers: corpo === undefined ? {} : { "content-type": "application/json" },
    ...(corpo === undefined ? {} : { body: JSON.stringify(corpo) }),
  });
  const dados = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error((dados as { erro?: string }).erro ?? "Não consegui falar com o servidor.");
  return dados as T;
}

export function Seguranca() {
  const eu = useEu();
  const qc = useQueryClient();
  const [assistente, setAssistente] = useState(false);
  const [desligando, setDesligando] = useState(false);
  const [trocandoSenha, setTrocandoSenha] = useState(false);

  const acessos = useQuery({
    queryKey: ["acessos"],
    queryFn: () => pedir<{ acessos: Acesso[] }>("/auth/acessos"),
  });

  const ativo = eu.segundoFatorAtivo;
  const exigidoPeloCrm = eu.segundoFatorExigido && !ativo;

  return (
    <Pagina titulo="Segurança" descricao="Como a sua conta é protegida, e onde ela está aberta agora.">
      {exigidoPeloCrm && (
        <Secao ordem={0}>
          <Cartao className="enotes-estado-atencao">
            <div className="enotes-estado-linha">
              <span aria-hidden className="sinal sinal-atencao" />
              <div className="enotes-estado-texto">
                <strong>O EnturOS CRM pede verificação em duas etapas</strong>
                <span>
                  A sua conta no CRM já usa a verificação em duas etapas, e aqui ela ainda não está
                  ligada. Ative agora para os dois sistemas ficarem no mesmo nível de proteção.
                </span>
              </div>
              <Botao variante="primario" tamanho="sm" onClick={() => setAssistente(true)}>Ativar agora</Botao>
            </div>
          </Cartao>
        </Secao>
      )}

      <Secao titulo="Verificação em duas etapas" ordem={1}>
        <Cartao>
          <CartaoTopo
            icone={<ShieldCheck size={15} />}
            titulo={ativo ? "Ligada" : "Desligada"}
            descricao={ativo
              ? "Ao entrar, além da senha o Notes pede um código de seis números que só o seu celular tem."
              : "Hoje a sua senha é a única coisa entre a sua conta e quem descobrir ela."}
            acao={
              <Selo tom={ativo ? "bom" : "atencao"}>
                <span aria-hidden className={`sinal ${ativo ? "sinal-bom" : "sinal-atencao"}`} />
                {ativo ? "ativa" : "não ativa"}
              </Selo>
            }
          />
          <div className="enotes-acoes">
            {ativo
              ? <Botao variante="secundario" tamanho="sm" onClick={() => setDesligando(true)}>Desligar a verificação</Botao>
              : <Botao variante="primario" onClick={() => setAssistente(true)}>Ativar em três minutos <ChevronRight size={14} /></Botao>}
          </div>
        </Cartao>
      </Secao>

      <Secao titulo="Senha" ordem={2}>
        <Cartao>
          <CartaoTopo icone={<KeyRound size={15} />} titulo="Trocar a senha"
            descricao="Ao trocar, todas as sessões abertas caem, inclusive esta. Você entra de novo com a senha nova." />
          <div className="enotes-acoes">
            <Botao variante="secundario" tamanho="sm" onClick={() => setTrocandoSenha(true)}>Trocar a senha</Botao>
          </div>
        </Cartao>
      </Secao>

      <Secao titulo="Onde a sua conta está aberta" ordem={3}>
        {acessos.isLoading ? <Carregando /> : acessos.isError ? (
          <Erro mensagem={(acessos.error as Error).message} aoTentarDeNovo={() => void acessos.refetch()} />
        ) : (
          <Cartao>
            <CartaoTopo icone={<Laptop size={15} />} titulo="Sessões abertas"
              descricao="Se você não reconhece um destes, encerre e troque a sua senha." />
            <ul className="enotes-itens">
              {(acessos.data?.acessos ?? []).map((a) => (
                <li key={a.id}>
                  <Laptop size={15} aria-hidden />
                  <span className="enotes-item-texto">{aparelhoLegivel(a.userAgent)}</span>
                  <span className="carimbo num">{a.ip ?? "sem endereço"}</span>
                  <span className="carimbo">aberta em {dataHora(a.criadoEm)}</span>
                  <Botao tamanho="sm" variante="fantasma"
                    onClick={() => void pedir("/auth/acessos/revogar", { id: a.id }).then(() => acessos.refetch())}>
                    <Trash2 size={13} /> Encerrar
                  </Botao>
                </li>
              ))}
            </ul>
          </Cartao>
        )}
      </Secao>

      {assistente && (
        <Assistente
          aoFechar={() => setAssistente(false)}
          aoAtivar={() => { setAssistente(false); void qc.invalidateQueries(); location.reload(); }}
        />
      )}

      {desligando && (
        <DesligarSegundoFator aoFechar={() => setDesligando(false)} aoDesligar={() => location.reload()} />
      )}

      {trocandoSenha && (
        <TrocarSenha aoFechar={() => setTrocandoSenha(false)} />
      )}
    </Pagina>
  );
}

/** "Chrome no Mac" em vez da linha inteira do navegador, que ninguém lê. */
function aparelhoLegivel(ua: string | null): string {
  if (!ua) return "Aparelho desconhecido";
  const navegador = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome"
    : /Safari\//.test(ua) ? "Safari" : /Firefox\//.test(ua) ? "Firefox" : "Navegador";
  const sistema = /iPhone|iPad/.test(ua) ? "iPhone" : /Android/.test(ua) ? "Android"
    : /Mac OS X/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "aparelho";
  return `${navegador} no ${sistema}`;
}

// ---------------------------------------------------------------------------
// O assistente: cinco passos, um por tela.
// ---------------------------------------------------------------------------
type Passo = "porque" | "baixar" | "apontar" | "provar" | "codigos" | "pronto";
/** Os passos NUMERADOS. "porque" abre o assistente e não é passo: contar ele
 *  fazia a barra dizer "3 de 5" enquanto o título dizia "Passo 2". */
const ORDEM: Passo[] = ["baixar", "apontar", "provar", "codigos"];

function Assistente({ aoFechar, aoAtivar }: { aoFechar: () => void; aoAtivar: () => void }) {
  const [passo, setPasso] = useState<Passo>("porque");
  const [qr, setQr] = useState<{ qrDataUrl: string | null; segredo: string } | null>(null);
  const [codigo, setCodigo] = useState("");
  const [codigos, setCodigos] = useState<string[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const preparar = async () => {
    if (qr) return;
    setErro(null);
    try {
      setQr(await pedir<{ qrDataUrl: string | null; segredo: string }>("/auth/2fa/preparar", {}));
    } catch (e) { setErro((e as Error).message); }
  };

  const confirmar = async () => {
    setErro(null); setOcupado(true);
    try {
      const r = await pedir<{ codigosDeRecuperacao: string[] }>("/auth/2fa/confirmar", { codigo });
      setCodigos(r.codigosDeRecuperacao);
      setPasso("codigos");
    } catch (e) { setErro((e as Error).message); }
    finally { setOcupado(false); }
  };

  const indice = ORDEM.indexOf(passo);

  return (
    <Modal aberto aoFechar={aoFechar} titulo="Verificação em duas etapas" largura="34rem">
      {indice >= 0 && (
        <div className="enotes-passo-progresso">
          <Medidor valor={indice + 1} maximo={ORDEM.length} rotulo="" tom="marca" mostrarNumero={false} />
          <span className="carimbo">passo {indice + 1} de {ORDEM.length}</span>
        </div>
      )}

      {passo === "porque" && (
        <div className="enotes-passo">
          <h3>Uma proteção a mais, simples de usar</h3>
          <p>
            Com a verificação em duas etapas, entrar na sua conta passa a pedir a sua senha <strong>e</strong> um
            código de seis números que só o seu celular tem. <strong>Se alguém descobrir a sua senha, ainda assim
            não consegue entrar.</strong>
          </p>
          <p className="enotes-dica-passo">
            A ativação leva uns três minutos, e você só precisa do celular por perto.
          </p>
          <div className="enotes-passo-botoes">
            <Botao variante="fantasma" onClick={aoFechar}>Agora não</Botao>
            <Botao variante="primario" onClick={() => setPasso("baixar")}>Quero ativar <ChevronRight size={14} /></Botao>
          </div>
        </div>
      )}

      {passo === "baixar" && (
        <div className="enotes-passo">
          <h3>Passo 1: baixe o aplicativo de códigos</h3>
          <p>
            No seu <strong>celular</strong>, abra a loja de aplicativos e procure por
            <strong> Google Authenticator</strong>. Toque em instalar e espere terminar.
          </p>
          <div className="enotes-lojas">
            <a href="https://apps.apple.com/br/app/google-authenticator/id388497605" target="_blank" rel="noreferrer">
              <Smartphone size={16} aria-hidden /><strong>iPhone</strong><span>App Store</span>
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" rel="noreferrer">
              <Smartphone size={16} aria-hidden /><strong>Android</strong><span>Google Play</span>
            </a>
          </div>
          <p className="enotes-dica-passo">
            É de graça e não pede cadastro. Se você já usa outro aplicativo de códigos, como Authy ou
            Microsoft Authenticator, ele também serve.
          </p>
          <div className="enotes-passo-botoes">
            <Botao variante="fantasma" onClick={() => setPasso("porque")}>Voltar</Botao>
            <Botao variante="primario" onClick={() => { void preparar(); setPasso("apontar"); }}>
              Já instalei <ChevronRight size={14} />
            </Botao>
          </div>
        </div>
      )}

      {passo === "apontar" && (
        <div className="enotes-passo">
          <h3>Passo 2: aponte a câmera para o quadrado</h3>
          <p>
            No aplicativo que você acabou de instalar, toque no sinal de mais e escolha
            <strong> Ler código QR</strong>. Depois aponte a câmera do celular para o quadrado abaixo.
          </p>
          {!qr && <p className="enotes-dica-passo">Preparando o código…</p>}
          {qr?.qrDataUrl && (
            <img className="enotes-qr" src={qr.qrDataUrl} alt="Código QR da verificação em duas etapas" width={220} height={220} />
          )}
          {qr && (
            // Sem a imagem, a chave manual não pode ficar escondida atrás de um
            // clique: ela passa a ser o único caminho, e some com a ativação.
            <details className="enotes-manual" open={!qr.qrDataUrl}>
              <summary>{qr.qrDataUrl ? "A câmera não lê? Digite a chave à mão" : "Digite a chave à mão"}</summary>
              {!qr.qrDataUrl && (
                <p className="enotes-dica-passo">
                  Não consegui desenhar o quadrado desta vez. A chave abaixo faz o mesmo trabalho.
                </p>
              )}
              <code className="enotes-codigo">{qr.segredo}</code>
              <p className="enotes-dica-passo">
                No aplicativo, escolha inserir chave manualmente e cole exatamente estes caracteres.
              </p>
            </details>
          )}
          {erro && <p className="enotes-erro-passo"><TriangleAlert size={14} aria-hidden /> {erro}</p>}
          <div className="enotes-passo-botoes">
            <Botao variante="fantasma" onClick={() => setPasso("baixar")}>Voltar</Botao>
            <Botao variante="primario" disabled={!qr} onClick={() => setPasso("provar")}>
              O aplicativo já mostra um código <ChevronRight size={14} />
            </Botao>
          </div>
        </div>
      )}

      {passo === "provar" && (
        <div className="enotes-passo">
          <h3>Passo 3: digite o código que apareceu</h3>
          <p>
            O aplicativo mostra seis números que trocam a cada trinta segundos. Digite os que estão na
            tela agora. <strong>Este passo existe para provar que o seu aplicativo funciona</strong>, antes de a
            verificação passar a valer.
          </p>
          <Campo rotulo="Código de seis números" htmlFor="codigo2fa" erro={erro}>
            <Entrada id="codigo2fa" className="campo num enotes-codigo-entrada" inputMode="numeric"
              autoComplete="one-time-code" maxLength={6} placeholder="000000"
              value={codigo} onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => { if (e.key === "Enter" && codigo.length === 6) void confirmar(); }} />
          </Campo>
          <div className="enotes-passo-botoes">
            <Botao variante="fantasma" onClick={() => setPasso("apontar")}>Voltar</Botao>
            <Botao variante="primario" ocupado={ocupado} disabled={codigo.length !== 6} onClick={() => void confirmar()}>
              Confirmar
            </Botao>
          </div>
        </div>
      )}

      {passo === "codigos" && (
        <PainelDeCodigos codigos={codigos} aoConcluir={() => setPasso("pronto")} />
      )}

      {passo === "pronto" && (
        <div className="enotes-passo enotes-passo-fim">
          <CircleCheck size={44} aria-hidden />
          <h3>Tudo pronto</h3>
          <p>
            A partir de agora, entrar no Notes pede a sua senha e o código do aplicativo. Se você trocar de
            celular, use um dos códigos de emergência que acabou de guardar.
          </p>
          <div className="enotes-passo-botoes">
            <Botao variante="primario" onClick={aoAtivar}>Fechar</Botao>
          </div>
        </div>
      )}
    </Modal>
  );
}

function PainelDeCodigos({ codigos, aoConcluir }: { codigos: string[]; aoConcluir: () => void }) {
  const [guardei, setGuardei] = useState(false);
  const [copiado, setCopiado] = useState(false);

  const baixar = () => {
    const texto = `Códigos de emergência do EnturOS Notes\nGerados em ${new Date().toLocaleString("pt-BR")}\n\nCada código funciona UMA única vez.\n\n${codigos.join("\n")}\n`;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([texto], { type: "text/plain;charset=utf-8" }));
    a.download = "codigos-de-emergencia-enturos-notes.txt";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const imprimir = () => {
    const j = window.open("", "_blank", "width=600,height=700");
    if (!j) return;
    j.document.write(`<html><head><title>Códigos de emergência do EnturOS Notes</title></head>
      <body style="font-family:sans-serif;padding:32px">
      <h2>Códigos de emergência do EnturOS Notes</h2>
      <p>Cada código funciona <strong>uma única vez</strong>. Guarde este papel em lugar seguro.</p>
      <pre style="font-size:18px;line-height:2">${codigos.join("\n")}</pre>
      </body></html>`);
    j.document.close();
    j.print();
  };

  return (
    <div className="enotes-passo">
      <h3>Passo 4: guarde os seus códigos de emergência</h3>
      <p className="enotes-aviso-forte">
        <strong>Guarde estes {codigos.length} códigos em lugar seguro</strong>, no papel ou no seu gerenciador de
        senhas. Se você perder o celular, qualquer um deles entra no lugar do código do aplicativo.
        <strong> Cada um funciona uma única vez.</strong>
      </p>
      <div className="enotes-codigos-grade">
        {codigos.map((c) => <span key={c} className="num">{c}</span>)}
      </div>
      <div className="enotes-passo-botoes enotes-passo-botoes-tres">
        <Botao variante="fantasma" tamanho="sm" onClick={imprimir}><Printer size={14} /> Imprimir</Botao>
        <Botao variante="fantasma" tamanho="sm"
          onClick={() => { void navigator.clipboard.writeText(codigos.join("\n")); setCopiado(true); }}>
          <Copy size={14} /> {copiado ? "Copiado" : "Copiar"}
        </Botao>
        <Botao variante="fantasma" tamanho="sm" onClick={baixar}><Download size={14} /> Baixar</Botao>
      </div>
      <label className="enotes-confirmacao">
        <input type="checkbox" checked={guardei} onChange={(e) => setGuardei(e.target.checked)} />
        <span>Guardei os meus códigos de emergência em lugar seguro.</span>
      </label>
      <div className="enotes-passo-botoes">
        <Botao variante="primario" disabled={!guardei} onClick={aoConcluir}>Concluir</Botao>
      </div>
    </div>
  );
}

function DesligarSegundoFator({ aoFechar, aoDesligar }: { aoFechar: () => void; aoDesligar: () => void }) {
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const desligar = async () => {
    setErro(null); setOcupado(true);
    try { await pedir("/auth/2fa/desligar", { senha }); aoDesligar(); }
    catch (e) { setErro((e as Error).message); }
    finally { setOcupado(false); }
  };

  return (
    <Modal aberto aoFechar={aoFechar} titulo="Desligar a verificação em duas etapas?"
      descricao="A sua senha volta a ser a única proteção da conta. Para confirmar que é você, digite a senha."
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>Manter ligada</Botao>
          <Botao variante="perigo" ocupado={ocupado} disabled={!senha} onClick={() => void desligar()}>Desligar</Botao>
        </>
      }>
      <Campo rotulo="Sua senha" htmlFor="senhaDesligar" erro={erro}>
        <Entrada id="senhaDesligar" type="password" autoComplete="current-password"
          value={senha} onChange={(e) => setSenha(e.target.value)} />
      </Campo>
    </Modal>
  );
}

function TrocarSenha({ aoFechar }: { aoFechar: () => void }) {
  const [form, setForm] = useState({ atual: "", nova: "", repetida: "" });
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const trocar = async () => {
    if (form.nova !== form.repetida) { setErro("A nova senha e a repetição não são iguais."); return; }
    setErro(null); setOcupado(true);
    try {
      await pedir("/auth/senha", { senhaAtual: form.atual, senhaNova: form.nova });
      // A própria sessão caiu junto, e é esse o ponto da troca de senha.
      location.assign("/entrar");
    } catch (e) { setErro((e as Error).message); }
    finally { setOcupado(false); }
  };

  return (
    <Modal aberto aoFechar={aoFechar} titulo="Trocar a senha"
      descricao="Ao trocar, todas as sessões caem, inclusive esta. Você entra de novo com a senha nova."
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>Cancelar</Botao>
          <Botao variante="primario" ocupado={ocupado}
            disabled={!form.atual || form.nova.length < 12} onClick={() => void trocar()}>
            <LogOut size={14} /> Trocar e sair
          </Botao>
        </>
      }>
      <div className="enotes-form">
        <Campo rotulo="Senha atual" htmlFor="senhaAtual">
          <Entrada id="senhaAtual" type="password" autoComplete="current-password"
            value={form.atual} onChange={(e) => setForm({ ...form, atual: e.target.value })} />
        </Campo>
        <Campo rotulo="Senha nova" htmlFor="senhaNova" dica="Pelo menos doze caracteres. Uma frase que só você usa vale mais que letra trocada por número.">
          <Entrada id="senhaNova" type="password" {...CAMPO_SEGREDO} minLength={12}
            value={form.nova} onChange={(e) => setForm({ ...form, nova: e.target.value })} />
        </Campo>
        <Campo rotulo="Repita a senha nova" htmlFor="senhaRepetida" erro={erro}>
          <Entrada id="senhaRepetida" type="password" {...CAMPO_SEGREDO}
            value={form.repetida} onChange={(e) => setForm({ ...form, repetida: e.target.value })} />
        </Campo>
      </div>
    </Modal>
  );
}
